// The one color scheme every report chart and the species map use.
//
// Charts used to pick colors three different ways - a pastel array indexed
// with % 8 (pie), an HSL hash of the species name (stacked bar) and a rainbow
// interpolation (line) - so a species changed color from chart to chart and
// several came out fluorescent.
//
// SERIES_COLORS is a validated categorical palette: eight hues in a fixed
// order chosen so neighbors stay distinguishable under protanopia and
// deuteranopia, with a lightness band that reads on the app's white surface.
// The order is the safety mechanism - never reorder, extend or cycle it. Three
// slots (aqua, yellow, magenta) sit below 3:1 contrast on white, so a chart
// using them must also show values another way: tooltips, a legend with
// counts, or a table.
//
// Colors follow the species, not its rank in the current chart: the eight
// slots go to the organization's most-caught species over all time (the
// ranking comes from the unfiltered summary), so a filter that hides one
// species never repaints the others. Species past the eighth share a neutral
// "Other" gray, and the non-species results each have a fixed neutral.

export const SERIES_COLORS = [
  "#2a78d6", // blue
  "#eb6834", // orange
  "#1baf7a", // aqua
  "#eda100", // yellow
  "#e87ba4", // magenta
  "#008300", // green
  "#4a3aa7", // violet
  "#e34948", // red
];

export const OTHER_COLOR = "#b5b3ab";

// Fixed neutrals for results that are not a species. Darker means more
// informative: an unknown is still a mosquito; an empty cell is nothing.
export const NEUTRAL_COLORS = {
  unknown: "#6b6a65",
  bycatch: "#9a988f",
  unclassified: "#d0cfc8",
  empty: "#e1e0d9",
  dirty: "#e1e0d9",
};

// Chart chrome, so axes and gridlines recede behind the data.
export const CHART_INK = {
  primary: "#0b0b0b",
  secondary: "#52514e",
  muted: "#898781",
  grid: "#e1e0d9",
  axis: "#c3c2b7",
};

const CATEGORY_LABELS = {
  "unknown species": "unknown",
  bycatch: "bycatch",
  "bycatch cell": "bycatch",
  "empty cell": "empty",
  "dirty cell": "dirty",
  "not classified": "unclassified",
  "": "unclassified",
};

// The kind of result a report label stands for: "species" or one of the
// neutral categories. Accepts the old labels ("Bycatch cell", "") too.
export const categoryOfLabel = (label) =>
  CATEGORY_LABELS[String(label ?? "").trim().toLowerCase()] ?? "species";

/**
 * Colors for an organization's species.
 *
 * `rankedLabels` is the organization's species, most-caught first, over all
 * time - not the current chart's. Returns colorOf(label) and isOther(label);
 * a species that did not get one of the eight slots is "other".
 */
export const buildSpeciesColorScale = (rankedLabels = []) => {
  const slots = new Map();
  rankedLabels
    .filter((label) => categoryOfLabel(label) === "species")
    .slice(0, SERIES_COLORS.length)
    .forEach((label, i) => slots.set(normalize(label), SERIES_COLORS[i]));

  const colorOf = (label) => {
    const category = categoryOfLabel(label);
    if (category !== "species") return NEUTRAL_COLORS[category];
    return slots.get(normalize(label)) ?? OTHER_COLOR;
  };
  const isOther = (label) =>
    categoryOfLabel(label) === "species" && !slots.has(normalize(label));

  return { colorOf, isOther, slotCount: slots.size };
};

/**
 * `scale` once it holds the organization's ranking. Until that is in (or for
 * an organization with none yet, or when it failed to load), a scale from the
 * chart's own species, `rankedLabels` most-caught first, so they are not all
 * Other.
 */
export const withOwnRanking = (scale, rankedLabels) =>
  scale.slotCount ? scale : buildSpeciesColorScale(rankedLabels);

const normalize = (label) => String(label ?? "").trim().toLowerCase();

/**
 * The org-wide species ranking from [{species, count}] rows (an unfiltered
 * species pivot): species labels, most-caught first, ties broken
 * alphabetically so the order is stable.
 */
export const rankSpecies = (summaryRows = []) => {
  const totals = new Map();
  summaryRows.forEach(({ species, count }) => {
    if (!species || categoryOfLabel(species) !== "species") return;
    totals.set(species, (totals.get(species) ?? 0) + (Number(count) || 0));
  });
  return [...totals.entries()]
    .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0], undefined, { sensitivity: "base" }))
    .map(([species]) => species);
};
